import { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import { ButtonSecondary } from "@/components/Buttons";
import api from "@/lib/api";
import { FileSearch, FileText, Loader2, Search, ShieldCheck, ListChecks } from "lucide-react";

const COMMITMENTS = [
    {
        icon: ShieldCheck,
        title: "Genuinely independent",
        text: "Testing is performed by third-party laboratories that are independent of Grayhaven Research. We do not grade our own work.",
    },
    {
        icon: FileText,
        title: "Documented, always",
        text: "Results are recorded in Certificates of Analysis tied to specific batches and lots — retained, organised, and available on request.",
    },
    {
        icon: ListChecks,
        title: "Built to be searched",
        text: "Our documentation library is structured around materials, batches and lots, so records can be found and verified without friction.",
    },
];

export default function Testing() {
    const [documents, setDocuments] = useState([]);
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const res = await api.get("/documents", { params: query ? { q: query } : {} });
                if (!cancelled) setDocuments(res.data);
            } catch {
                if (!cancelled) setDocuments([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        const t = setTimeout(load, 250);
        return () => {
            cancelled = true;
            clearTimeout(t);
        };
    }, [query]);

    return (
        <div data-testid="testing-page">
            <PageHeader
                eyebrow="Verification"
                title="Testing & Documentation"
                description="Independent testing and complete documentation — the evidence behind every material we supply."
            />

            <section className="pb-24 sm:pb-28 px-6 lg:px-8">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
                    {COMMITMENTS.map((item, i) => (
                        <Reveal key={item.title} delay={i * 0.1}>
                            <div
                                data-testid={`testing-commitment-${i}`}
                                className="card-lift h-full bg-surface border border-gold/20 p-8 rounded-sm hover:border-gold/50 hover:bg-surface2"
                            >
                                <item.icon className="h-7 w-7 text-gold" strokeWidth={1.5} />
                                <h2 className="mt-6 font-serif text-2xl font-light text-brand">{item.title}</h2>
                                <p className="mt-4 text-sm leading-relaxed text-mist">{item.text}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* COA Library */}
            <section className="py-24 sm:py-28 px-6 lg:px-8 bg-white/70 border-y border-stone-200">
                <div className="max-w-7xl mx-auto">
                    <Reveal className="max-w-2xl">
                        <p className="eyebrow">Document Library</p>
                        <h2 className="mt-6 font-serif text-3xl sm:text-4xl font-light tracking-tight text-brand">
                            Certificates of Analysis
                        </h2>
                        <p className="mt-6 text-base leading-relaxed text-mist">
                            COAs and laboratory documentation for Grayhaven materials will be published here, organised
                            by material and batch. The library is already searchable — records appear as they are
                            published.
                        </p>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <div className="mt-12 relative max-w-xl">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-brand0" />
                            <input
                                type="search"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search by material, document, or batch / lot…"
                                data-testid="coa-search-input"
                                className="w-full bg-white border border-stone-200 focus:border-wine/60 rounded-sm pl-11 pr-4 py-3.5 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-colors duration-300"
                            />
                        </div>
                    </Reveal>

                    <div className="mt-10" data-testid="coa-library">
                        {loading ? (
                            <div className="flex items-center gap-3 text-mist text-sm py-12">
                                <Loader2 className="h-4 w-4 animate-spin text-gold" /> Loading documents…
                            </div>
                        ) : documents.length > 0 ? (
                            <div className="divide-y divide-slate-800/70 border-y border-stone-200">
                                {documents.map((doc) => (
                                    <div
                                        key={doc.id}
                                        data-testid={`coa-row-${doc.id}`}
                                        className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 py-6 items-center"
                                    >
                                        <div>
                                            <p className="font-serif text-xl text-brand">{doc.title}</p>
                                            <p className="mt-1 text-sm text-mist">
                                                {doc.material_name} · Batch/Lot {doc.batch_lot} · Issued {doc.issued_date}
                                            </p>
                                        </div>
                                        {doc.file_url && (
                                            <a
                                                href={doc.file_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid={`coa-download-${doc.id}`}
                                                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-wine hover:text-wine-deep transition-colors duration-300"
                                            >
                                                <FileText className="h-4 w-4" /> View COA
                                            </a>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="border border-dashed border-gold/25 rounded-sm py-20 px-8 text-center">
                                <FileSearch className="h-10 w-10 text-gold/60 mx-auto" strokeWidth={1.25} />
                                <h3 className="mt-6 font-serif text-2xl font-light text-brand">
                                    The COA library is being prepared
                                </h3>
                                <p className="mt-4 text-sm leading-relaxed text-mist max-w-md mx-auto">
                                    Published certificates will appear here. In the meantime, documentation for any
                                    Grayhaven material is available directly on request.
                                </p>
                                <div className="mt-8">
                                    <ButtonSecondary to="/contact" data-testid="coa-request-btn">
                                        Request Documentation
                                    </ButtonSecondary>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* What a COA contains */}
            <section className="py-24 sm:py-32 px-6 lg:px-8">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                    <Reveal>
                        <p className="eyebrow">Reading The Evidence</p>
                        <h2 className="mt-6 font-serif text-3xl sm:text-4xl font-light tracking-tight text-brand">
                            What a Certificate of Analysis tells you
                        </h2>
                        <p className="mt-6 text-base leading-relaxed text-mist">
                            A Certificate of Analysis (COA) is the laboratory record for a specific batch of material.
                            It connects the material you receive to the test results behind it — which is why batch and
                            lot traceability matters.
                        </p>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <ul className="divide-y divide-slate-800/70 border-y border-stone-200">
                            {[
                                "Material identification and batch / lot reference",
                                "Analytical methods used and the laboratory that performed them",
                                "Purity and identity results as measured, not summarised",
                                "Date of analysis and document authorisation",
                            ].map((item) => (
                                <li key={item} className="py-5 flex items-start gap-4">
                                    <span className="mt-2 h-px w-6 bg-gold shrink-0" aria-hidden="true" />
                                    <span className="text-sm leading-relaxed text-slate-700">{item}</span>
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
